/* eslint-disable vue/one-component-per-file */
// legacy routes redirect components

import { computed, defineComponent, h, watchEffect, onBeforeMount } from 'vue'
import { useRouter } from 'vue-router'
import { gql } from '@apollo/client/core'
import { useQuery } from '/@/core/graphql'
import { useCurrentDesigner } from '/@/data/designer/current'
import { type Class } from '../common/school/types'
import { useSchoolApi } from '../common/school/api'
import { useRoute } from 'vue-router'

export const ViziRedirect = (routeName: string) =>
  defineComponent({
    props: {
      viziId: {
        type: String,
        required: true,
      },
    },
    setup(props) {
      const router = useRouter()
      const route = useRoute()
      const originalRouteName = route.name?.toString() ?? ''
      const originalQuery = route.query
      const currentRouteName = computed(() => route.name?.toString() ?? '')
      if (props.viziId.startsWith('create')) {
        router.replace({ name: 'vizi-create' })
      }
      const { result, loading, error } = useQuery<{
        mockup: { id: string; designer?: { id: string; nickname: string } }
      }>(
        gql`
          query getViziForRedirect($id: String!) {
            mockup(id: $id) {
              id
              designer {
                id
                nickname
              }
            }
          }
        `,
        {
          id: props.viziId,
        },
      )

      watchEffect(() => {
        if (error.value) {
          // todo: redirect to 404
        }
        // confirm that the route has not changed before redirecting
        if (
          !loading.value &&
          result.value &&
          currentRouteName.value === originalRouteName
        ) {
          const vizi = computed(() => result.value?.mockup)
          router.replace({
            name: routeName,
            params: {
              viziId: props.viziId,
              nickname: vizi.value?.designer?.nickname,
            },
            query: originalQuery,
          })
        }
      })

      return () => h(() => null)
    },
  })

export const ProjectRedirect = (routeName: string) =>
  defineComponent({
    props: {
      projectId: {
        type: String,
        required: true,
      },
    },
    setup(props) {
      const router = useRouter()
      const route = useRoute()
      const originalRouteName = route.name?.toString() ?? ''
      const originalQuery = route.query
      const currentRouteName = computed(() => route.name?.toString() ?? '')
      const { result, loading, error } = useQuery<{
        project: { id: string; designer?: { id: string; nickname: string } }
      }>(
        gql`
          query getProjectForRedirect($id: String!) {
            project(id: $id) {
              id
              designer {
                id
                nickname
              }
            }
          }
        `,
        {
          id: props.projectId,
        },
      )

      watchEffect(() => {
        if (error.value) {
          // todo: redirect to 404
        }
        // confirm that the route has not changed before redirecting
        if (
          !loading.value &&
          result.value &&
          currentRouteName.value === originalRouteName
        ) {
          const project = computed(() => result.value?.project)
          router.replace({
            name: routeName,
            params: {
              projectId: props.projectId,
              nickname: project.value?.designer?.nickname,
            },
            query: originalQuery,
          })
        }
      })

      return () => h(() => null)
    },
  })

export const PaletteRedirect = (routeName: string) =>
  defineComponent({
    props: {
      paletteId: {
        type: String,
        required: true,
      },
    },
    setup(props) {
      const router = useRouter()
      const route = useRoute()
      const originalRouteName = route.name?.toString() ?? ''
      const originalQuery = route.query
      const currentRouteName = computed(() => route.name?.toString() ?? '')
      const { result, loading, error } = useQuery<{
        palette: { id: string; designer?: { id: string; nickname: string } }
      }>(
        gql`
          query getPaletteForRedirect($id: String!) {
            palette(id: $id) {
              id
              designer {
                id
                nickname
              }
            }
          }
        `,
        {
          id: props.paletteId,
        },
      )

      watchEffect(() => {
        if (error.value) {
          // todo: redirect to 404
        }
        // confirm that the route has not changed before redirecting
        if (
          !loading.value &&
          result.value &&
          currentRouteName.value === originalRouteName
        ) {
          const palette = computed(() => result.value?.palette)
          router.replace({
            name: routeName,
            params: {
              paletteId: props.paletteId,
              nickname: palette.value?.designer?.nickname,
            },
            query: originalQuery,
          })
        }
      })

      return () => h(() => null)
    },
  })

export const PortfolioRedirect = (routeName: string) =>
  defineComponent({
    setup() {
      const { designer } = useCurrentDesigner()
      const router = useRouter()

      watchEffect(() => {
        if (!designer.value?.nickname) {
          // todo: redirect to 404
          return
        }

        router.replace({
          name: routeName,
          params: {
            nickname: designer.value.nickname,
          },
        })
      })

      return () => h(() => null)
    },
  })

export const CompleteClass = () =>
  defineComponent({
    props: {
      classId: {
        type: String,
        required: true,
      },
    },
    setup(props) {
      const { result } = useQuery<{ class: Class }>(
        gql`
          query getDesignerClassInfo($id: String!) {
            class(id: $id) {
              id
              goal {
                id
                designerInfo {
                  id
                }
              }
              designerInfo {
                id
              }
            }
          }
        `,
        {
          id: computed(() => props.classId),
        },
      )

      const theClass = computed(() => result.value?.class)

      const schoolApi = useSchoolApi()
      const router = useRouter()

      watchEffect(async () => {
        if (
          !theClass.value?.designerInfo?.id ||
          !theClass.value.goal.designerInfo?.id
        ) {
          // todo: redirect to 404
          return
        }

        await schoolApi.updateClassStatus({
          designerClassId: theClass.value.designerInfo.id,
          designerGoalId: theClass.value.goal.designerInfo.id,
          isCompleted: true,
        })

        router.replace({
          name: 'class-completion',
          params: {
            designerClassId: theClass.value.designerInfo.id,
          },
        })
      })

      return () => h(() => null)
    },
  })

export const HelpHubRedirect = () =>
  defineComponent({
    setup() {
      onBeforeMount(async () => {
        window.location.href = 'https://help.spoak.com'
      })
    },
  })

export const CommunityRedirect = () =>
  defineComponent({
    setup() {
      onBeforeMount(async () => {
        window.location.href = 'https://community.spoak.com'
      })
    },
  })
